/*!
 * Copyright © 2018-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import { isElementNull } from '../../../../../../utils';
import { SelectVehicle } from '../../../../store/actions';
import {
  ENotificationSettingsTypes,
  ENotificationTypes,
  INotification,
  INotificationClosingParams,
  INotificationDetails,
  INotificationDetailsParams,
  INotificationDetailsResponse,
  INotificationResponse
} from '../../models';
import { AlertService, NotificationsService, RedirectService } from '../../services';
import {
  AcknowledgePanic,
  AddNotifications,
  AddNotificationsSuccess,
  CloseNonPanicNotification,
  ENotificationsActions,
  GetNotificationInfo,
  GetNotificationInfoSuccess,
  GetPersistedNotifications,
  GetPersistedNotificationsSuccess,
  InitializeNotifications,
  ReviewPanic
} from '../actions';

@Injectable()
export class NotificationsEffects {
  public initializeNotifications$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<InitializeNotifications>(ENotificationsActions.InitializeNotifications),
      filter(() => isPlatformBrowser(this._platformId)),
      switchMap(() => [new GetPersistedNotifications()])
    )
  );

  public addNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AddNotifications>(ENotificationsActions.AddNotifications),
      map((action: AddNotifications) => action.payload),
      map(notificationResponse => this._convertToNotificationModel(<INotificationResponse>notificationResponse)),
      map(notification => new AddNotificationsSuccess(notification))
    )
  );

  public closeNonPanicNotification$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<CloseNonPanicNotification>(ENotificationsActions.CloseNonPanicNotification),
        map((action: CloseNonPanicNotification) => action.payload),
        tap((params: INotificationClosingParams) => {
          this._notificationsService.updatePersistedNotification(this._convertToServiceModel(params.notification)).subscribe();
          if (params.clicked) {
            if (params.notification.settingType === ENotificationSettingsTypes.exceededAlertLimit) {
              this._redirectService.redirectToUrl(`/Alerts/?v=l`);
            } else if (params.notification.type === ENotificationTypes.videoAlert) {
              this._redirectService.redirectToVideoEventPage(+params.notification.id);
            }
          }
        })
      ),
    { dispatch: false }
  );

  public reviewPanic$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<ReviewPanic>(ENotificationsActions.ReviewPanic),
      map((action: ReviewPanic) => action.payload),
      tap((param: INotification) => {
        this._notificationsService.reviewPanic(this._convertToServiceModel(param)).pipe(take(1)).subscribe();
      }),
      map(notification => new SelectVehicle(`v${notification.vehicleId}`))
    )
  );

  public acknowledgePanic$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<AcknowledgePanic>(ENotificationsActions.AcknowledgePanic),
        map((action: AcknowledgePanic) => action.payload),
        tap((param: INotification) => {
          this._notificationsService.acknowledgePanic(this._convertToServiceModel(param)).pipe(take(1)).subscribe();
        })
      ),
    { dispatch: false }
  );

  public getPersistedNotifications$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetPersistedNotifications>(ENotificationsActions.GetPersistedNotifications),
      switchMap(() =>
        this._notificationsService.getPersistedNotifications().pipe(
          map(notifications => notifications.map(n => this._convertToNotificationModel(n))),
          map(notifications => new GetPersistedNotificationsSuccess(notifications))
        )
      )
    )
  );

  public getNotificationInfo$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetNotificationInfo>(ENotificationsActions.GetNotificationInfo),
      map(action => action.payload),
      switchMap((parameters: INotificationDetailsParams) => this._alertService.getAlertLogInfo(parameters)),
      map((notification: INotificationDetailsResponse) => {
        const notificationsConverted: INotificationDetails = {
          notificationRuleName: notification.AlertRuleName,
          notificationType: notification.AlertType,
          notificationTypeName: notification.AlertTypeName,
          vehicleName: notification.VehicleName,
          vehicleNumber: notification.VehicleNumber,
          driverName: notification.DriverName,
          driverEmail: notification.DriverEmail,
          driverPhone: notification.DriverPhone,
          triggerType: notification.TriggerType,
          threshold: notification.Threshold,
          dateTime: notification.DateTime,
          location: notification.Location,
          coordinates: {
            Latitude: notification.Latitude,
            Longitude: notification.Longitude
          },
          confirmedBy: notification.ConfirmedBy
        };

        return new GetNotificationInfoSuccess(notificationsConverted);
      })
    )
  );

  constructor(
    private readonly _actions$: Actions,
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _notificationsService: NotificationsService,
    private readonly _redirectService: RedirectService,
    private readonly _alertService: AlertService
  ) {}

  private _convertToNotificationModel(notificationResponse: INotificationResponse): INotification {
    const notification: INotification = {
      id: notificationResponse.Id,
      type: notificationResponse.meta,
      title: notificationResponse.title,
      vehicleId: notificationResponse.vid,
      coordinates: {
        Latitude: notificationResponse.lat,
        Longitude: !isElementNull(notificationResponse.lng) ? notificationResponse.lng : notificationResponse.long
      },
      badge: notificationResponse.badge,
      db: notificationResponse.db,
      settingType: notificationResponse.type,
      content: notificationResponse.body,
      temporaryPopup: true
    };

    return notification;
  }

  private _convertToServiceModel(notification: INotification): INotificationResponse {
    const notificationResponse: INotificationResponse = {
      Id: notification.id,
      body: notification.content,
      title: notification.title,
      meta: notification.type,
      vid: notification.vehicleId,
      lat: notification.coordinates ? notification.coordinates.Latitude : null,
      lng: notification.coordinates ? notification.coordinates.Longitude : null,
      db: notification.db,
      type: notification.settingType,
      badge: notification.badge
    };

    return notificationResponse;
  }
}
